// De keuzeopties achter de velden 'Materiaal' en 'Coachadvies' in de
// materialenmodal: standaardmaterialen en coachadviezen, per groep getagd met
// welk van de twee het is. De modal filtert op die tag en zet elke soort in
// zijn eigen keuzeveld; het groepslabel draagt de soort nog omdat de tests en
// andere afnemers er per groep aan af moeten kunnen lezen wat erin zit.
//
// TWEE BRONNEN. De materialen zijn een vaste lijst van producten die
// organisaties inkopen (standardMaterials.js). De adviezen komen uit de
// interventiebibliotheek die de app toch al ophaalt — zie coachAdviceOptions.js
// voor welk deel daarvan als coachadvies telt en waarom. Daarom is dit een
// functie van de bibliotheek en geen constante: zonder bibliotheek zijn er wel
// materialen en geen adviezen.
//
// WAAROM EEN OPTIEWAARDE EN NIET DE NAAM ZELF. De vorige versie was een vrij
// tekstveld met een datalist en codeerde de keuze als 'Titel - Categorie'. Dat
// werkte zolang alles een materiaal was; met twee soorten in dezelfde lijst is
// een naam niet langer uniek. De waarde draagt daarom de soort mee
// ('advice:Korter douchen'), zodat 'wat is dit' een lookup is en geen gok op
// basis van de tekst.
//
// KIND ONTBREEKT OP BESTAANDE DOCUMENTEN. Elk Materials-document van voor deze
// wijziging heeft geen Kind-veld. Dat leest als 'material' — het waren immers
// allemaal materialen — en niet als onbekend, anders zou de hele bestaande
// voorraad ineens als advies renderen.
//
// AI-CONTEXT: See docs/features/materials-co2-circulariteit.md

import STANDARD_MATERIALS from './standardMaterials'
import { coachAdviceGroups } from './coachAdviceOptions'

const MATERIAL = 'material'
const ADVICE = 'advice'

// 'Staat niet in de lijst': een product waarvoor de meetstandaard geen
// equivalent heeft. Geen catalogusoptie maar een sentinel, want er hoort geen
// standaardnaam bij -- de eigen naam van de organisatie IS dan de naam, en
// 'Hoort bij' is de enige koppeling aan een maatregel.
// AI-CONTEXT: See docs/features/materials-own-name-and-article-number.md
const CUSTOM_VALUE = 'custom:'

/**
 * @typedef {'material'|'advice'} CatalogKind
 *
 * @typedef {object} CatalogOption
 * @property {string} value - Stabiele optiewaarde, '<kind>:<titel>'
 * @property {CatalogKind} kind
 * @property {string} title
 * @property {string} category
 * @property {string} unit - Inkoopeenheid (materiaal) of registratie-eenheid uit de standaard (advies)
 * @property {string} code - Interventiecode; alleen gevuld bij een advies
 * @property {number|null} savingEurPerYear - Besparing op de energierekening; alleen bij een advies
 *
 * @typedef {object} CatalogGroup
 * @property {CatalogKind} kind
 * @property {string} label - Kop boven de groep in de keuzelijst
 * @property {string} category
 * @property {Array<CatalogOption>} options
 */

/**
 * @param {CatalogKind} kind
 * @param {string} title
 * @returns {string}
 */
const catalogValue = (kind, title) => `${kind}:${title}`

/** @type {Array<CatalogGroup>} */
const MATERIAL_GROUPS = STANDARD_MATERIALS.map((group) => ({
    kind: MATERIAL,
    label: `Materiaal · ${group.category}`,
    category: group.category,
    options: group.items.map((item) => ({
        value: catalogValue(MATERIAL, item.title),
        kind: MATERIAL,
        title: item.title,
        category: group.category,
        unit: item.unit || '',
        code: '',
        savingEurPerYear: null,
    })),
}))

/**
 * Materialen plus de coachadviezen uit de meegegeven bibliotheek.
 *
 * @param {object|null} library - Uit useInterventieLibrary; null bij laden of fout
 * @returns {Array<CatalogGroup>}
 */
const buildCatalogGroups = (library) => [
    ...MATERIAL_GROUPS,
    ...coachAdviceGroups(library).map((group) => ({
        kind: ADVICE,
        label: `Coachadvies · ${group.category}`,
        category: group.category,
        options: group.items.map((intervention) => ({
            value: catalogValue(ADVICE, intervention.name),
            kind: ADVICE,
            title: intervention.name,
            category: group.category,
            // De eenheid van een advies is de registratie-eenheid van de
            // standaard ('per huishouden', 'per graad te koud'), niet een
            // verpakking. Er valt dus ook nooit iets om te rekenen.
            unit: intervention.unit || '',
            code: intervention.code,
            savingEurPerYear: intervention.privateEurPerUnit,
        })),
    })),
]

/**
 * @param {Array<CatalogGroup>} groups
 * @param {string} value
 * @returns {CatalogOption|null}
 */
const findCatalogOption = (groups, value) => {
    if (!value) return null
    for (const group of groups || []) {
        const match = group.options.find((option) => option.value === value)
        if (match) return match
    }
    return null
}

/**
 * Wat een opgeslagen document is. Documenten van voor de coachadviezen hebben
 * geen Kind en zijn per definitie materialen.
 *
 * @param {{Kind?: string}|null} material
 * @returns {CatalogKind}
 */
const materialKindOf = (material) => (material?.Kind === ADVICE ? ADVICE : MATERIAL)

/**
 * @param {{Kind?: string}|null} material
 * @returns {boolean}
 */
const isAdvice = (material) => materialKindOf(material) === ADVICE

/**
 * De optiewaarde waarmee een opgeslagen document in de keuzelijst zou staan.
 * Of het er daadwerkelijk in staat is een vraag aan findCatalogOption: de
 * adviezen hangen aan een bibliotheek die er niet altijd is.
 *
 * @param {{Title?: string, Kind?: string}|null} material
 * @returns {string}
 */
const catalogValueForMaterial = (material) =>
    material?.Title ? catalogValue(materialKindOf(material), material.Title) : ''

export {
    MATERIAL,
    ADVICE,
    CUSTOM_VALUE,
    MATERIAL_GROUPS,
    buildCatalogGroups,
    findCatalogOption,
    catalogValue,
    catalogValueForMaterial,
    materialKindOf,
    isAdvice,
}
